import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Link } from 'react-router-dom';
import { FolderOpen, FileText } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import CookieBanner from '@/components/CookieBanner';
import PageBreadcrumbs from '@/components/PageBreadcrumbs';
import { Helmet } from 'react-helmet-async';

interface Category {
  name: string;
  slug: string;
  count: number;
}

export default function BlogCategories() {
  const { data: categories = [], isLoading } = useQuery<Category[]>({
    queryKey: ['blog-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('category')
        .eq('published', true);
      
      if (error) throw error;
      
      const categoryCounts: Record<string, number> = data.reduce((acc, post) => {
        if (post.category) {
          acc[post.category] = (acc[post.category] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>);
      
      return Object.entries(categoryCounts).map(([name, count]) => ({
        name,
        slug: name.toLowerCase().replace(/\s+/g, '-'),
        count,
      })).sort((a, b) => b.count - a.count);
    }
  });

  return (
    <>
      <Helmet>
        <title>Blog Kategorien | Cyberversicherung für Ärzte</title>
        <meta name="description" content="Durchsuchen Sie alle Blog-Kategorien und finden Sie relevante Artikel zur Cyberversicherung für Ärzte und medizinische Einrichtungen." />
      </Helmet>

      <Navigation />
      <CookieBanner />
      <PageBreadcrumbs items={[
        { label: "Home", path: "/" },
        { label: "Blog", path: "/blog" },
        { label: "Kategorien" }
      ]} />

      {/* Wellenförmiger Hintergrund */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-accent/5"></div>
        <div className="absolute top-0 left-0 right-0 h-[500px] bg-gradient-to-b from-accent/10 via-accent/5 to-transparent"></div>
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-accent/20 rounded-full blur-[120px]"></div>
        <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[100px]"></div>
      </div>

      <div className="min-h-screen relative z-10">
        {/* Header */}
        <div className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 border-b border-white/5">
          <div className="container mx-auto text-center max-w-4xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-accent via-primary to-accent bg-clip-text text-transparent">
              Alle Kategorien
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground/90">
              Finden Sie Artikel nach Themenbereich sortiert
            </p>
          </div>
        </div>

        {/* Kategorien-Grid */}
        <div className="container mx-auto px-4 py-12 sm:py-16">
          {isLoading ? (
            <div className="flex justify-center items-center min-h-[400px]">
              <div className="text-muted-foreground">Lade Kategorien...</div>
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Keine Kategorien gefunden</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-6xl mx-auto">
              {categories.map((category) => (
                <Link
                  key={category.slug}
                  to={`/blog?category=${category.slug}`}
                  className="group relative p-6 rounded-xl border-2 border-accent/40 
                    bg-white/[0.115] backdrop-blur-xl
                    hover:border-accent/60 hover:scale-[1.02]
                    transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <div className="flex items-start justify-between mb-3">
                    <FolderOpen className="w-8 h-8 text-accent" />
                    <span className="text-xs px-2 py-1 rounded-full bg-accent/30 text-accent font-medium">
                      {category.count} Artikel
                    </span>
                  </div>
                  
                  <h2 className="text-xl font-bold mb-3 group-hover:text-accent transition-colors">
                    {category.name}
                  </h2>
                  
                  <div className="flex items-center gap-2 text-sm text-muted-foreground group-hover:text-accent transition-colors">
                    <FileText className="w-4 h-4" />
                    Artikel anzeigen →
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
}
